import type {
  WikiRevision,
  WikiLinkRenderer,
  WikiRevisionComparison,
} from "../wiki-types";
import { WikiAuthorName } from "../wiki-byline";
import { RevisionDate } from "./wiki-revision-date";
import { shortRevisionId } from "./short-revision-id";
import { Button } from "@softmax/design-system";
import { styles } from "../wiki.styles";
import * as stylex from "@stylexjs/stylex";
import {
  spacing,
  colors,
  fonts,
  fontSizes,
  radii,
} from "@softmax/design-system/tokens.stylex";

export function WikiRevisionRow({
  revision,
  older,
  isCurrent,
  canEdit,
  revertingId,
  onDiff,
  onRevert,
  renderLink,
}: Readonly<{
  revision: WikiRevision;
  older: WikiRevision | undefined;
  isCurrent: boolean;
  canEdit: boolean;
  revertingId: string | null;
  onDiff(value: WikiRevisionComparison): void;
  onRevert(revisionId: string): void;
  renderLink: WikiLinkRenderer;
}>) {
  return (
    <li {...stylex.props(localStyles.revision)}>
      <div {...stylex.props(localStyles.revisionText)}>
        <div {...stylex.props(styles.byline)}>
          <WikiAuthorName author={revision.author} renderLink={renderLink} />
          <span>·</span>
          <RevisionDate iso={revision.createdAt} />
          <span>·</span>
          <span>{revision.note || "No summary"}</span>
          {isCurrent && (
            <span {...stylex.props(localStyles.badge)}>Current</span>
          )}
        </div>
        <p {...stylex.props(localStyles.revisionId)}>
          {shortRevisionId(revision.id)}
        </p>
      </div>
      <div {...stylex.props(styles.actions)}>
        <Button
          size="sm"
          variant="quiet"
          disabled={!older}
          onClick={() => {
            if (older) onDiff({ older, newer: revision });
          }}
          aria-label={`Diff ${shortRevisionId(revision.id)}`}
        >
          Diff
        </Button>
        <Button
          size="sm"
          disabled={!canEdit || isCurrent || revertingId !== null}
          onClick={() => onRevert(revision.id)}
          aria-label={`Revert to ${shortRevisionId(revision.id)}`}
        >
          {revertingId === revision.id ? "Reverting…" : "Revert to this"}
        </Button>
      </div>
    </li>
  );
}
const localStyles = stylex.create({
  revision: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "start",
    justifyContent: "space-between",
    gap: spacing.space12,
    paddingBlock: spacing.space16,
    borderBottomWidth: 1,
    borderBottomStyle: "solid",
    borderBottomColor: colors.border,
  },
  revisionText: { minWidth: 0 },
  revisionId: {
    margin: 0,
    marginTop: spacing.space4,
    color: colors.muted,
    fontFamily: fonts.mono,
    fontSize: fontSizes.caption,
    overflowWrap: "anywhere",
  },
  badge: {
    paddingInline: spacing.space8,
    borderRadius: radii.pill,
    borderWidth: 1,
    borderStyle: "solid",
    borderColor: colors.border,
    backgroundColor: colors.surfaceAlt,
    fontSize: fontSizes.caption,
    textTransform: "uppercase",
    fontWeight: 600,
  },
});
